import * as React from "react";

import { SearchOutlined } from "@ant-design/icons";
import { Select } from "antd";
import { InputStatus } from "antd/es/_util/statusUtils";
import {
    SelectProps as AntdSelectProps,
    SelectValue as AntdSelectValue,
    LabeledValue,
} from "antd/es/select";
import PQueue from "p-queue";

import { t } from "../../utils/i18n";
import { assertNever } from "../../utils/obj";

import * as theme from "./theme.css";

const TIMEOUT_MILLI = 500;

interface TypeaheadOptionGroup<T> {
    type: "Group";
    value: React.ReactNode;
    options: Array<TypeaheadOption<T>>;
}

interface TypeaheadOption<T> {
    type: "Option";
    value: T;
}

type TypeaheadOptionOrGroup<T> = TypeaheadOption<T> | TypeaheadOptionGroup<T>;

/**
 * An autocomplete widget, based on a regular input field. The dropdown disappears after a timeout if the focus leaves the input field.
 *
 * Props:
 * - `placeholder` (string, maybe null): the content of the input placeholder.
 * - `value` (string, maybe null): the content of the input field.
 * - `formatOption` (function, required): format an option. Should take an option as parameter. Should return a string.
 * - `disabled` (boolean, optional): if true, the input is disabled.
 * - `onSearch` (function, required): invoked when the search value changes. Should accept the new value (as string) as parameter, as well as a callback. The callback accepts an array of options or groups, and will feed them to the autocomplete drop-down.
 * - `onSelect` (function, required): callback invoked when the user picked an option. Should accept the selected option as parameter.
 * - `noResultsMessage` (string, optional): string to display when the search gave no result.
 *
 * State:
 * - `searchValue`: the value typed while the user is completing the field. Takes precedence over the props' `value` field if set. Cleared when the user selects a value or the input field loses focus.
 * - `optionOrGroups`: the list of options or groups to feed to the autocomplete dropdown, as obtained from `onSearch`.
 */
class InputWithTypeahead<OPTION> extends React.PureComponent<
    InputWithTypeaheadProps<OPTION>,
    InputWithTypeaheadState<OPTION>
> {
    private nextSearchValue = "";
    private searchValue = "-"; // Initial value to force initial search
    private queue: PQueue = new PQueue({ concurrency: 1 });
    private timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    constructor(props: InputWithTypeaheadProps<OPTION>) {
        super(props);
        this.state = {
            optionOrGroups: [],
            optionIndex: {},
        };
    }
    componentWillUnmount() {
        this.queue.clear();
        if (this.timeoutHandle !== undefined) {
            clearTimeout(this.timeoutHandle);
            this.timeoutHandle = undefined;
        }
    }
    render() {
        const {
            id,
            selectSize,
            value,
            onBlur,
            status,
            formatOption,
            placeholder,
            className,
            dropdownRender,
            addonAfter,
            dropdownMatchSelectWidth,
            getPopupContainer,
            allowClear,
            fieldRef,
            disabled: maybeDisabled,
            isAutoFocused,
        } = this.props;
        const disabled = maybeDisabled !== undefined ? maybeDisabled : false;

        const { optionOrGroups, optionIndex } = this.state;

        const hasValue = value !== undefined && value !== null;
        const labelInValue = hasValue && typeof value !== "string";

        // To correctly display no value and clear the value when searching,
        // we need to set the mode to multiple. Otherwise autoClearSearchValue is ignored.
        // suffixIcon prop has no effect if mode is set
        const autoClearSearchValue = !hasValue;
        const mode = addonAfter || hasValue ? undefined : "multiple";
        // sanitizes the value so that undefined is treated as an array.
        const sanitizedValue = hasValue ? value : [];

        return (
            <div
                className={`${theme.autocompleteContainer} ${
                    className ? className : theme.standardContainer
                }`}
            >
                <Select
                    ref={fieldRef}
                    id={`${id}-input`}
                    optionLabelProp="children"
                    filterOption={false}
                    showSearch={true}
                    suffixIcon={
                        <div className={theme.suffix}>
                            {addonAfter}
                            <SearchOutlined />
                        </div>
                    }
                    autoClearSearchValue={autoClearSearchValue}
                    mode={mode}
                    size={selectSize}
                    placeholder={placeholder}
                    disabled={disabled}
                    dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                    dropdownRender={
                        dropdownRender ? this.renderDropdown : undefined
                    }
                    notFoundContent={
                        this.searchValue.length > 0 ? t("NoResults") : null
                    }
                    getPopupContainer={getPopupContainer}
                    labelInValue={labelInValue}
                    value={sanitizedValue}
                    onBlur={onBlur}
                    onSearch={this.handleSearch}
                    status={status}
                    allowClear={allowClear}
                    onSelect={(value: AntdSelectValue) => {
                        let key: string;
                        if (typeof value === "string") {
                            key = value;
                        } else if (
                            typeof (value as LabeledValue)?.key === "string"
                        ) {
                            key = (value as LabeledValue).key!;
                        } else {
                            throw new Error(
                                "Unrecognized selected value " +
                                    JSON.stringify(value)
                            );
                        }
                        const option = optionIndex[key];
                        if (option) {
                            this.handleSelect(option.value);
                        }
                    }}
                    onDropdownVisibleChange={(open) => {
                        if (open) {
                            this.handleSearch("");
                        }
                    }}
                    autoFocus={isAutoFocused}
                >
                    {optionOrGroups.map((option, index1) => {
                        switch (option.type) {
                            case "Group":
                                return (
                                    <Select.OptGroup
                                        key={index1.toString()}
                                        label={option.value}
                                    >
                                        {option.options.map(
                                            (option, index2) => {
                                                return (
                                                    <Select.Option
                                                        key={`${index1}.${index2}`}
                                                        value={`${index1}.${index2}`}
                                                    >
                                                        {formatOption(
                                                            option.value
                                                        )}
                                                    </Select.Option>
                                                );
                                            }
                                        )}
                                    </Select.OptGroup>
                                );
                            case "Option":
                                return (
                                    <Select.Option
                                        key={index1.toString()}
                                        value={index1.toString()}
                                    >
                                        {formatOption(option.value)}
                                    </Select.Option>
                                );
                            default:
                                return assertNever(option);
                        }
                    })}
                </Select>
            </div>
        );
    }
    private renderDropdown = (
        menu: React.ReactElement,
        props?: AntdSelectProps
    ): React.ReactElement => {
        const { dropdownRender } = this.props;
        if (dropdownRender) {
            return dropdownRender(menu, props, this);
        } else {
            return menu;
        }
    };
    private handleSearch = (searchValue: string) => {
        const userAgent = window.navigator.userAgent;
        const isIE = /MSIE|Trident\//.test(userAgent);

        this.nextSearchValue = searchValue;
        if (isIE) {
            if (this.timeoutHandle !== undefined) {
                clearTimeout(this.timeoutHandle);
            }
            this.timeoutHandle = setTimeout(this.doSearch, TIMEOUT_MILLI);
        } else {
            this.doSearch();
        }
    };
    private doSearch = () => {
        this.timeoutHandle = undefined;
        if (this.nextSearchValue !== this.searchValue) {
            const realSearchValue = (this.searchValue = this.nextSearchValue);
            // Clear the queue to take the latest search into account.
            this.queue.clear();
            this.queue.add(() =>
                this.props.onSearch(
                    realSearchValue,
                    (optionOrGroups: Array<TypeaheadOptionOrGroup<OPTION>>) => {
                        if (
                            optionOrGroups !== null &&
                            this.searchValue === realSearchValue
                        ) {
                            this.updateOptionsOrGroups(optionOrGroups);
                        }
                    }
                )
            );
        }
    };
    private updateOptionsOrGroups = (
        optionOrGroups: Array<TypeaheadOptionOrGroup<OPTION>>
    ) => {
        let actualOptionOrGroups: Array<TypeaheadOptionOrGroup<OPTION>>;
        if (
            optionOrGroups.length === 0 &&
            this.props.noResultsMessage !== undefined
        ) {
            actualOptionOrGroups = [
                {
                    type: "Group",
                    value: <div>{this.props.noResultsMessage}</div>,
                    options: [],
                },
            ];
        } else {
            actualOptionOrGroups = optionOrGroups;
        }
        const optionIndex: { [key: string]: TypeaheadOption<OPTION> } = {};
        optionOrGroups.forEach((option, index1) => {
            switch (option.type) {
                case "Group":
                    option.options.forEach((option, index2) => {
                        optionIndex[`${index1}.${index2}`] = option;
                    });
                    break;
                case "Option":
                    optionIndex[index1.toString()] = option;
                    break;
                default:
                    return assertNever(option);
            }
            return;
        });
        this.setState({
            optionOrGroups: actualOptionOrGroups,
            optionIndex,
        });
    };
    private handleSelect = (item: OPTION) => {
        this.searchValue = "";
        this.setState(
            {
                optionOrGroups: [],
                optionIndex: {},
            },
            () => {
                this.props.onSelect(item);
            }
        );
    };
}

interface InputWithTypeaheadProps<OPTION> {
    id: string;
    selectSize?: "middle" | "large" | "small";
    placeholder?: React.ReactNode;
    value: string | LabeledValue | undefined;
    disabled?: boolean;
    status?: InputStatus;
    formatOption: (item: OPTION) => React.ReactNode;
    onSearch: (
        searchValue: string,
        callback: (
            optionOrGroups: Array<TypeaheadOptionOrGroup<OPTION>>
        ) => void
    ) => Promise<void>;
    onSelect: (item: OPTION) => void;
    onBlur?: () => void;
    noResultsMessage?: string;
    className?: string;
    fieldRef?: React.RefCallback<unknown>;
    dropdownRender?: (
        menu: React.ReactElement,
        props?: AntdSelectProps,
        input?: InputWithTypeahead<OPTION>
    ) => React.ReactElement;
    // TODO handle addons differently
    addonAfter?: React.ReactNode;
    dropdownMatchSelectWidth?: boolean;
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    allowClear?: boolean;
    isAutoFocused?: boolean;
}

interface InputWithTypeaheadState<OPTION> {
    optionOrGroups: Array<TypeaheadOptionOrGroup<OPTION>>;
    optionIndex: { [key: string]: TypeaheadOption<OPTION> };
}

function CreateSuggestion<T>(props: {
    label: string;
    input: InputWithTypeahead<T>;
    onClick: () => void;
}) {
    const { onClick, label } = props;
    return (
        <div className={theme.createSuggestion}>
            <a
                href="#"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    onClick();
                }}
            >
                {label}
            </a>
        </div>
    );
}

class ReadOnlyTypeahead extends React.PureComponent<ReadOnlyTypeaheadProps> {
    render = () => {
        const { id, className, selectSize, placeholder, addonAfter, value } =
            this.props;
        const labelInValue =
            value !== undefined && value !== null && typeof value !== "string";
        return (
            <div
                className={`${theme.autocompleteContainer} ${
                    className ? className : theme.standardContainer
                }`}
            >
                <Select
                    id={`${id}-input`}
                    disabled={true}
                    suffixIcon={
                        <div className={theme.suffix}>
                            {addonAfter}
                            <SearchOutlined type="search" />
                        </div>
                    }
                    size={selectSize}
                    placeholder={placeholder}
                    labelInValue={labelInValue}
                    value={value}
                />
            </div>
        );
    };
}

interface ReadOnlyTypeaheadProps {
    id: string;
    className?: string;
    selectSize?: "middle" | "large" | "small";
    placeholder?: string;
    addonAfter?: React.ReactNode;
    value?: string | LabeledValue;
}
export type {
    TypeaheadOption,
    TypeaheadOptionGroup,
    TypeaheadOptionOrGroup,
    InputWithTypeaheadProps,
    InputWithTypeaheadState,
    ReadOnlyTypeaheadProps,
};
export { InputWithTypeahead, ReadOnlyTypeahead, CreateSuggestion };
